import React, { useState, useMemo } from "react";
import { CSS_CLASSES, MESSAGES } from "../constants/app";
import { conversions } from "../utils";
import { getPatientMobileValidationError } from "../utils/validatePatientMobile";
import type {
  Patient,
  PersonalInformation,
  PhysicalDetails,
  ContactInformation,
} from "../types";
import { Spinner } from "./loading/LoadingIndicators";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"] as const;
const GENDERS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
] as const;
const ETHNICITIES = [
  "Gujarati",
  "Marathi",
  "Punjabi",
  "Bengali",
  "South Indian (Tamil / Telugu / Kannada / Malayalam)",
  "Rajasthani / Marwari",
  "Other Indian ethnicity",
  "Non-Indian",
] as const;
const RELIGIONS = [
  "Hindu",
  "Muslim",
  "Jain",
  "Christian",
  "Sikh",
  "Buddhist",
  "Other",
  "Prefer not to say",
] as const;

interface PatientFormState {
  full_name: string;
  gender: string;
  dob: string;
  ethnicity: string;
  religion: string;
  height: string;
  weight: string;
  waist: string;
  hip: string;
  blood_group: string;
  email: string;
  phone: string;
  pin_code: string;
  district: string;
  address: string;
  abha_id: string;
}

const emptyFormState: PatientFormState = {
  full_name: "",
  gender: "",
  dob: "",
  ethnicity: "",
  religion: "",
  height: "",
  weight: "",
  waist: "",
  hip: "",
  blood_group: "",
  email: "",
  phone: "",
  pin_code: "",
  district: "",
  address: "",
  abha_id: "",
};

function patientToFormState(patient: Patient | null): PatientFormState {
  if (!patient) return emptyFormState;
  const pi = patient.personal_information;
  const pd = patient.physical_details;
  const ci = patient.contact_information;
  return {
    full_name: pi?.full_name ?? "",
    gender: pi?.gender ?? "",
    dob: pi?.dob ?? "",
    ethnicity: pi?.ethnicity ?? "",
    religion: pi?.religion ?? "",
    height: pd?.height ? String(pd.height) : "",
    weight: pd?.weight ? String(pd.weight) : "",
    waist: pd?.waist ? String(pd.waist) : "",
    hip: pd?.hip ? String(pd.hip) : "",
    blood_group: pd?.blood_group ?? "",
    email: ci?.email ?? "",
    phone: ci?.phone ?? "",
    pin_code: ci?.pin_code ?? "",
    district: ci?.district ?? "",
    address: ci?.address ?? "",
    abha_id: pi?.abha_id ?? "",
  };
}

export interface PatientFormPayload {
  personal_information: PersonalInformation;
  physical_details: PhysicalDetails;
  contact_information: ContactInformation;
}

export interface PatientFormProps {
  mode: "add" | "edit";
  initialPatient?: Patient | null;
  onSubmit: (payload: PatientFormPayload) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
  title?: string;
  subtitle?: string;
}

export default function PatientForm({
  mode,
  initialPatient = null,
  onSubmit,
  onCancel,
  submitLabel,
  title,
  subtitle,
}: PatientFormProps) {
  const initial = useMemo(
    () => patientToFormState(initialPatient ?? null),
    [initialPatient]
  );
  const [formData, setFormData] = useState<PatientFormState>(initial);
  const [errors, setErrors] = useState<
    Partial<Record<keyof PatientFormState, string>> & { general?: string }
  >({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field: keyof PatientFormState, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    if (errors.general) {
      setErrors((prev) => ({ ...prev, general: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = MESSAGES.VALIDATION.REQUIRED_FIELD;
    }
    if (!formData.gender) {
      newErrors.gender = MESSAGES.VALIDATION.REQUIRED_FIELD;
    }
    if (!formData.dob.trim()) {
      newErrors.dob = MESSAGES.VALIDATION.REQUIRED_FIELD;
    } else {
      const dobDate = new Date(formData.dob);
      if (isNaN(dobDate.getTime()) || dobDate > new Date()) {
        newErrors.dob = "Please enter a valid date of birth";
      }
    }
    if (formData.height.trim()) {
      const h = parseFloat(formData.height);
      if (isNaN(h) || h <= 0 || h > 300) {
        newErrors.height = "Please enter a valid height (1–300 cm)";
      }
    }
    if (formData.weight.trim()) {
      const w = parseFloat(formData.weight);
      if (isNaN(w) || w <= 0 || w > 500) {
        newErrors.weight = "Please enter a valid weight (1–500 kg)";
      }
    }
    const phoneError = getPatientMobileValidationError(formData.phone);
    if (phoneError) {
      newErrors.phone = phoneError;
    }
    if (
      formData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      newErrors.email = MESSAGES.VALIDATION.INVALID_EMAIL;
    }
    if (formData.pin_code.trim() && !/^\d{6}$/.test(formData.pin_code.trim())) {
      newErrors.pin_code = "Please enter a valid 6-digit PIN code";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = (): PatientFormPayload => {
    const heightNum = formData.height.trim() ? parseFloat(formData.height) : 0;
    const weightNum = formData.weight.trim() ? parseFloat(formData.weight) : 0;
    const waistNum = formData.waist.trim() ? parseFloat(formData.waist) : undefined;
    const hipNum = formData.hip.trim() ? parseFloat(formData.hip) : undefined;
    const bmi =
      heightNum > 0 && weightNum > 0
        ? conversions.calculateBMI(heightNum, weightNum)
        : undefined;

    const personal_information: PersonalInformation = {
      full_name: formData.full_name.trim(),
      dob: formData.dob,
      gender: formData.gender,
      ...(formData.abha_id.trim() && { abha_id: formData.abha_id.trim() }),
      ...(formData.ethnicity.trim() && { ethnicity: formData.ethnicity.trim() }),
      ...(formData.religion.trim() && { religion: formData.religion.trim() }),
    };

    const physical_details: PhysicalDetails = {
      height: heightNum || 0,
      weight: weightNum || 0,
      ...(bmi !== undefined && { bmi }),
      ...(formData.blood_group && { blood_group: formData.blood_group }),
      ...(waistNum !== undefined && waistNum > 0 && { waist: waistNum }),
      ...(hipNum !== undefined && hipNum > 0 && { hip: hipNum }),
    };

    const contact_information: ContactInformation = {};
    if (formData.email.trim())
      contact_information.email = formData.email.trim();
    if (formData.phone.trim())
      contact_information.phone = formData.phone.trim();
    if (formData.pin_code.trim())
      contact_information.pin_code = formData.pin_code.trim();
    if (formData.district.trim())
      contact_information.district = formData.district.trim();
    if (formData.address.trim())
      contact_information.address = formData.address.trim();

    return {
      personal_information,
      physical_details,
      contact_information,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    setErrors((prev) => ({ ...prev, general: undefined }));
    try {
      await onSubmit(buildPayload());
    } catch (err: any) {
      setErrors((prev) => ({
        ...prev,
        general: err?.message ?? "Something went wrong",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const displaySubmitLabel =
    submitLabel ?? (mode === "add" ? "Save" : "Update");

  return (
    <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
      {/* Basic Information */}
      <div>
        <h2 className={CSS_CLASSES.FORM.SECTION_TITLE}>
          Basic Information
        </h2>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="full_name"
              className="mb-1 block text-sm font-medium text-guardian-header"
            >
              Full name <span className="text-red-500">*</span>
            </label>
            <input
              id="full_name"
              type="text"
              value={formData.full_name}
              onChange={(e) => handleChange("full_name", e.target.value)}
              placeholder="Enter full name"
              className={`${CSS_CLASSES.INPUTS.PATIENT} ${errors.full_name ? CSS_CLASSES.INPUTS.ERROR : ""}`}
              disabled={isLoading}
            />
            {errors.full_name && (
              <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>
                {errors.full_name}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="gender"
                className="mb-1 block text-sm font-medium text-guardian-header"
              >
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                id="gender"
                value={formData.gender}
                onChange={(e) => handleChange("gender", e.target.value)}
                className={`${CSS_CLASSES.INPUTS.PATIENT} ${errors.gender ? CSS_CLASSES.INPUTS.ERROR : ""}`}
                disabled={isLoading}
              >
                <option value="">Select gender</option>
                {GENDERS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>
              {errors.gender && (
                <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>
                  {errors.gender}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="dob"
                className="mb-1 block text-sm font-medium text-guardian-header"
              >
                Date of birth <span className="text-red-500">*</span>
              </label>
              <input
                id="dob"
                type="date"
                value={formData.dob}
                onChange={(e) => handleChange("dob", e.target.value)}
                max={new Date().toISOString().split("T")[0]}
                className={`${CSS_CLASSES.INPUTS.PATIENT} ${errors.dob ? CSS_CLASSES.INPUTS.ERROR : ""}`}
                disabled={isLoading}
              />
              {errors.dob && (
                <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>{errors.dob}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="ethnicity"
                className="mb-1 block text-sm font-medium text-guardian-header"
              >
                Ethnicity
              </label>
              <select
                id="ethnicity"
                value={formData.ethnicity}
                onChange={(e) => handleChange("ethnicity", e.target.value)}
                className={CSS_CLASSES.INPUTS.PATIENT}
                disabled={isLoading}
              >
                <option value="">Select ethnicity</option>
                {ETHNICITIES.map((ethnicity) => (
                  <option key={ethnicity} value={ethnicity}>
                    {ethnicity}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="religion"
                className="mb-1 block text-sm font-medium text-guardian-header"
              >
                Religion
              </label>
              <select
                id="religion"
                value={formData.religion}
                onChange={(e) => handleChange("religion", e.target.value)}
                className={CSS_CLASSES.INPUTS.PATIENT}
                disabled={isLoading}
              >
                <option value="">Select religion</option>
                {RELIGIONS.map((religion) => (
                  <option key={religion} value={religion}>
                    {religion}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Details */}
      <div>
        <h2 className={CSS_CLASSES.FORM.SECTION_TITLE}>
          Contact Details
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="phone"
              className="mb-1 block text-sm font-medium text-guardian-header"
            >
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              placeholder="10-digit mobile"
              className={`${CSS_CLASSES.INPUTS.PATIENT} ${errors.phone ? CSS_CLASSES.INPUTS.ERROR : ""}`}
              disabled={isLoading}
            />
            {errors.phone && (
              <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>
                {errors.phone}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="email"
              className="mb-1 block text-sm font-medium text-guardian-header"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              placeholder="patient@example.com"
              className={`${CSS_CLASSES.INPUTS.PATIENT} ${errors.email ? CSS_CLASSES.INPUTS.ERROR : ""}`}
              disabled={isLoading}
            />
            {errors.email && (
              <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>
                {errors.email}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="pin_code"
              className="mb-1 block text-sm font-medium text-guardian-header"
            >
              PIN code
            </label>
            <input
              id="pin_code"
              type="text"
              maxLength={6}
              value={formData.pin_code}
              onChange={(e) =>
                handleChange("pin_code", e.target.value.replace(/\D/g, ""))
              }
              placeholder="6-digit PIN code"
              className={`${CSS_CLASSES.INPUTS.PATIENT} ${errors.pin_code ? CSS_CLASSES.INPUTS.ERROR : ""}`}
              disabled={isLoading}
            />
            {errors.pin_code && (
              <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>
                {errors.pin_code}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="district"
              className="mb-1 block text-sm font-medium text-guardian-header"
            >
              District
            </label>
            <input
              id="district"
              type="text"
              value={formData.district}
              onChange={(e) => handleChange("district", e.target.value)}
              placeholder="Enter district"
              className={CSS_CLASSES.INPUTS.PATIENT}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>

      {/* Identification */}
      <div>
        <h2 className={CSS_CLASSES.FORM.SECTION_TITLE}>
          Identification
        </h2>
        <div>
          <label
            htmlFor="abha_id"
            className="mb-1 block text-sm font-medium text-guardian-header"
          >
            ABHA ID
          </label>
          <input
            id="abha_id"
            type="text"
            value={formData.abha_id}
            onChange={(e) => handleChange("abha_id", e.target.value)}
            className={CSS_CLASSES.INPUTS.PATIENT}
            disabled={isLoading}
          />
        </div>
      </div>

      {/* Physical Information */}
      <div>
        <h2 className={CSS_CLASSES.FORM.SECTION_TITLE}>
          Physical Information
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="height"
              className="mb-1 block text-sm font-medium text-guardian-header"
            >
              Height (cm)
            </label>
            <input
              id="height"
              type="number"
              min={1}
              max={300}
              step={0.1}
              value={formData.height}
              onChange={(e) => handleChange("height", e.target.value)}
              placeholder="e.g. 170"
              className={`${CSS_CLASSES.INPUTS.PATIENT} ${errors.height ? CSS_CLASSES.INPUTS.ERROR : ""}`}
              disabled={isLoading}
            />
            {errors.height && (
              <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>
                {errors.height}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="weight"
              className="mb-1 block text-sm font-medium text-guardian-header"
            >
              Weight (kg)
            </label>
            <input
              id="weight"
              type="number"
              min={1}
              max={500}
              step={0.1}
              value={formData.weight}
              onChange={(e) => handleChange("weight", e.target.value)}
              placeholder="e.g. 70"
              className={`${CSS_CLASSES.INPUTS.PATIENT} ${errors.weight ? CSS_CLASSES.INPUTS.ERROR : ""}`}
              disabled={isLoading}
            />
            {errors.weight && (
              <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>
                {errors.weight}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="blood_group"
              className="mb-1 block text-sm font-medium text-guardian-header"
            >
              Blood group
            </label>
            <select
              id="blood_group"
              value={formData.blood_group}
              onChange={(e) => handleChange("blood_group", e.target.value)}
              className={CSS_CLASSES.INPUTS.PATIENT}
              disabled={isLoading}
            >
              <option value="">Select blood group</option>
              {BLOOD_GROUPS.map((bg) => (
                <option key={bg} value={bg}>
                  {bg}
                </option>
              ))}
            </select>
          </div>
          <div />
          <div>
            <label
              htmlFor="waist"
              className="mb-1 block text-sm font-medium text-guardian-header"
            >
              Waist (cm)
            </label>
            <input
              id="waist"
              type="number"
              min={1}
              max={300}
              step={0.1}
              value={formData.waist}
              onChange={(e) => handleChange("waist", e.target.value)}
              className={CSS_CLASSES.INPUTS.PATIENT}
              disabled={isLoading}
            />
          </div>
          <div>
            <label
              htmlFor="hip"
              className="mb-1 block text-sm font-medium text-guardian-header"
            >
              Hip (cm)
            </label>
            <input
              id="hip"
              type="number"
              min={1}
              max={300}
              step={0.1}
              value={formData.hip}
              onChange={(e) => handleChange("hip", e.target.value)}
              className={CSS_CLASSES.INPUTS.PATIENT}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className={CSS_CLASSES.TEXT.ERROR}>{errors.general}</p>
        </div>
      )}

      <div className="flex flex-col-reverse gap-3 border-t border-guardian-20 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onCancel}
          className={`${CSS_CLASSES.BUTTONS.CANCEL_MODAL} sm:ml-0`}
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className={`${CSS_CLASSES.BUTTONS.PRIMARY_MODAL} sm:ml-auto ${
            isLoading ? CSS_CLASSES.BUTTONS.LOADING : ""
          }`}
        >
          {isLoading ? (
            <span className="inline-flex items-center gap-2">
              <Spinner size="sm" variant="onPrimary" />
              {mode === "add" ? "Saving..." : "Updating..."}
            </span>
          ) : (
            displaySubmitLabel
          )}
        </button>
      </div>
    </form>
  );
}

export { emptyFormState, patientToFormState };
export type { PatientFormState };
